import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './home.css';
import Hero from '../../components/Hero/Hero';
import PosterCarousel from '../../components/PosterCarousel/PosterCarousel';
import LaunchModal from '../../components/LaunchModal/LaunchModal';

const stats = [
  { value: '10000+', label: 'Students Mentored' },
  { value: '300+', label: 'GMC+GEC' },
  { value: '15+', label: ' Individual Experience' },
  { value: '100%', label: 'Dedication' },
];

const courses = [
  {
    label: 'Foundation',
    tag: 'Class 8-10',
    description: 'Building strong base for future success in competitive exams.',
    path: '/courses/foundation-olympiad',
    image:
      'mode/foundationn.png',
  },
  {
    label: 'Olympiad',
    tag: 'Class 8-12',
    description: 'Competitive excellence and advanced problem solving skills.',
    path: '/courses/olympiad',
    image:
      '/mode/olympiadd.png',
  },
  {
    label: 'NEET',
    tag: 'Medical',
    description: 'Rigorous preparation program for aspiring doctors, with focused training in  Biology, Chemistry, Physics',
    path: '/courses',
    image:
      'mode/neet.jpg',
  },
  {
    label: 'JEE (Main + Adv)',
    tag: 'Engineering',
    description: 'Targeting IITs and NITs with in-depth concept clarity and practice.',
    path: '/courses',
    image:
      '/mode/engineering.jpg',
  },
];

// ✅ Inline SVG icons (no FontAwesome needed)
const WifiIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M2.5 8.5C7.8 3.6 16.2 3.6 21.5 8.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M5.5 11.5c4-3.6 9-3.6 13 0"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M8.7 14.7c2.2-2 4.4-2 6.6 0"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <circle cx="12" cy="18" r="1.6" fill="currentColor" />
  </svg>
);

const CapIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M12 3 2 8l10 5 10-5-10-5Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path
      d="M6 10v6c0 2 3 4 6 4s6-2 6-4v-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
  </svg>
);

const HybridIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="7" cy="7" r="2" fill="currentColor" />
    <circle cx="17" cy="7" r="2" fill="currentColor" opacity="0.7" />
    <circle cx="12" cy="17" r="2" fill="currentColor" opacity="0.85" />
    <path
      d="M8.6 8.4 11 15M15.4 8.4 13 15"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const Home: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Expose function to open modal from Navigation
  React.useEffect(() => {
    (window as any).openCourseLaunchModal = () => setIsModalOpen(true);
    return () => {
      delete (window as any).openCourseLaunchModal;
    };
  }, []);

  return (
  <main className="home-page">
    <Helmet>
      <title>SCUBUS – Best JEE NEET and Olympiad Coaching Institute in Dwarka Delhi</title>
      <meta 
        name="description" 
        content="SCUBUS is a leading coaching institute in Dwarka for JEE, NEET & Olympiad preparation. Expert guidance, structured learning and focused academic support for students." 
      />
      <meta 
        name="keywords" 
        content="JEE coaching in Dwarka, NEET coaching in Dwarka, Olympiad coaching in Dwarka, JEE preparation Dwarka, NEET preparation Dwarka, Olympiad preparation Dwarka, best coaching institute in Dwarka, competitive exam coaching in Dwarka, JEE, NEET & Olympiad Coaching Institute in Dwarka" 
      />
      <link rel="canonical" href="https://www.scubus.com/" />
      
      {/* Open Graph */}
      <meta property="og:title" content="SCUBUS – Best JEE NEET and Olympiad Coaching Institute in Dwarka Delhi" />
      <meta property="og:description" content="SCUBUS is a leading coaching institute in Dwarka for JEE, NEET & Olympiad preparation. Expert guidance, structured learning and focused academic support for students." />
      <meta property="og:url" content="https://www.scubus.com/" />
      <meta property="og:type" content="website" />
      
      {/* Twitter Card */}
      <meta name="twitter:title" content="SCUBUS – Best JEE NEET and Olympiad Coaching Institute in Dwarka Delhi" />
      <meta name="twitter:description" content="SCUBUS is a leading coaching institute in Dwarka for JEE, NEET & Olympiad preparation. Expert guidance, structured learning and focused academic support for students." />
    </Helmet>

    <LaunchModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    <Hero />

    <section className="metrics-section">
      {stats.map((stat) => (
        <div key={stat.label} className="metric">
          <p className="metric-value">{stat.value}</p>
          <p className="metric-label">{stat.label}</p>
        </div>
      ))}
    </section>

    <section className="courses-section">
      <div className="section-heading">
        <h2>Our Courses</h2>
        <p>Comprehensive programs designed for every stage of your academic journey.</p>
        
      </div>

      <div className="courses-grid">
        {courses.map((course) => (
          <article key={course.label} className="course-card">
            <div className="course-image">
              <img src={course.image} alt={course.label} />
            </div>

            <div className="course-body">
              <span className="course-tag">{course.tag}</span>
              <h3>{course.label}</h3>
              <p>{course.description}</p>

              <Link to={course.path} className="course-cta">
                Learn More
              </Link>
            </div>
          </article>
        ))}
      </div>
    </section>

    {/* ✅ UPDATED: Flexible Learning Modes section to match your image */}
    <section className="modes-section">
      <div className="modes-wrap">
        {/* Left content */}
        <div className="modes-left">
          <div className="mode-illustration">
            <img
              src="/mode/mode.jpg"
              alt="Flexible learning modes illustration"
              loading="lazy"
            />
          </div>
          <div className="modes-text">
            <h2>Flexible Learning Modes</h2>
            <p>
              We understand that every student learns differently. Choose the mode that fits your lifestyle and learning
              preference.
            </p>
          </div>
        </div>

        {/* Right cards */}
        <div className="modes-right">
          <article className="mode-card">
            <div className="mode-icon" aria-hidden="true">
              <CapIcon />
            </div>
            <div className="mode-content">
              <h3>Offline Classroom</h3>
              <p>
                Traditional face-to-face learning with direct interaction with mentors, doubt resolution, and a competitive peer environment.
              </p>
            </div>
          </article>

          <article className="mode-card">
            <div className="mode-icon" aria-hidden="true">
              <WifiIcon />
            </div>
            <div className="mode-content">
              <h3>Online Live Sessions</h3>
              <p>
                Interactive classes from the comfort of your home, with anytime access to recorded lectures and digital study material.
              </p>
            </div>
          </article>

          <article className="mode-card">
            <div className="mode-icon" aria-hidden="true">
              <HybridIcon />
            </div>
            <div className="mode-content">
              <h3>Hybrid Learning System</h3>
              <p>
                Interactive  broadcasting learning 
              </p>
            </div>
          </article>
        </div>
      </div>
    </section>

    <PosterCarousel />

    <section id="cta-journey" className="cta-section">
      <div>
        <h2>Ready to start your journey?</h2>
        <p>
          Join S-CUBUS today and give your dreams the wings they deserve. Scholarships available for meritorious
          students.
        </p>
      </div>
      <div className="cta-actions">
        <a
          href="https://forms.gle/xdExtHf2Yss4ZaHq9"
          className="btn primary"
          target="_blank"
          rel="noopener noreferrer"
        >
          Apply for Scholarship
        </a>
        <Link to="/contact" className="btn secondary">
          Contact Us
        </Link>
      </div>
    </section>

  </main>
  );
};

export default Home;
